"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { getNavIcon } from "@/content/nav-icons";
import { mainNav } from "@/content/navigation";
import {
  getNavLinkClass,
  getNavPillHighlightClass,
  getNavUnderlineClass,
  type NavAccentContext,
} from "@/components/layout/nav-types";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type DesktopNavLinksProps = NavAccentContext & {
  isScrolled: boolean;
};

export function DesktopNavLinks({ isScrolled, ...ctx }: DesktopNavLinksProps) {
  const pathname = usePathname();
  const reduced = useReducedMotion();
  const [hoveredHref, setHoveredHref] = useState<string | null>(null);
  const pillHighlightClass = getNavPillHighlightClass(ctx, isScrolled);
  const underlineClass = getNavUnderlineClass(ctx, isScrolled);

  return (
    <nav
      className="hidden items-center gap-0.5 lg:flex"
      aria-label="Primary"
      onMouseLeave={() => setHoveredHref(null)}
    >
      {mainNav.map(({ href, label, iconOnly }) => {
        const active =
          href === "/"
            ? pathname === "/"
            : href === "/account"
              ? pathname === "/account"
              : pathname.startsWith(href);
        const Icon = getNavIcon(href);
        const showHoverPill = !reduced && hoveredHref === href && !active;

        const linkClass = cn(
          "relative rounded-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          iconOnly
            ? "inline-flex h-9 w-9 items-center justify-center"
            : "px-2 py-2 lg:px-2 xl:px-3.5",
          getNavLinkClass(active, ctx, isScrolled)
        );

        const content = (
          <>
            {active && (
              <motion.span
                layoutId="nav-active-pill"
                className={cn(
                  "absolute inset-0 rounded-full",
                  pillHighlightClass
                )}
                transition={{ type: "spring", stiffness: 380, damping: 32 }}
              />
            )}
            {showHoverPill && (
              <motion.span
                layoutId="nav-hover-pill"
                className={cn(
                  "absolute inset-0 rounded-full opacity-70",
                  pillHighlightClass
                )}
                transition={{ type: "spring", stiffness: 420, damping: 34 }}
              />
            )}
            <motion.span
              className="relative z-10 inline-flex items-center justify-center gap-1.5"
              whileHover={reduced ? undefined : { y: -1 }}
              transition={{ duration: 0.15 }}
            >
              <motion.span
                key={active ? `settle-${pathname}-${href}` : `icon-${href}`}
                className="inline-flex"
                initial={
                  active && !reduced
                    ? { opacity: 0.55, scale: 0.92 }
                    : false
                }
                animate={{ opacity: 1, scale: 1 }}
                whileHover={
                  reduced ? undefined : { scale: 1.12, rotate: -3 }
                }
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              >
                <Icon size={iconOnly ? 18 : 15} aria-hidden strokeWidth={2} />
              </motion.span>
              {!iconOnly ? <span>{label}</span> : null}
            </motion.span>
            {!iconOnly && active && (
              <motion.span
                layoutId="nav-underline"
                className={cn(
                  "absolute bottom-1 left-2.5 right-2.5 z-10 h-[2px] rounded-full xl:left-4 xl:right-4",
                  underlineClass
                )}
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
            {!iconOnly && !active && (
              <motion.span
                className={cn(
                  "absolute bottom-1 left-2.5 right-2.5 z-10 h-[2px] rounded-full opacity-0 xl:left-4 xl:right-4",
                  underlineClass
                )}
                initial={false}
                whileHover={reduced ? undefined : { opacity: 0.35 }}
                transition={{ duration: 0.2 }}
              />
            )}
          </>
        );

        if (iconOnly) {
          return (
            <Tooltip key={href} content={label} side="bottom">
              <Link
                href={href}
                aria-label={label}
                aria-current={active ? "page" : undefined}
                className={linkClass}
                onMouseEnter={() => setHoveredHref(href)}
              >
                {content}
              </Link>
            </Tooltip>
          );
        }

        return (
          <Link
            key={href}
            href={href}
            aria-current={active ? "page" : undefined}
            className={linkClass}
            onMouseEnter={() => setHoveredHref(href)}
          >
            {content}
          </Link>
        );
      })}
    </nav>
  );
}
